import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";
import PetsnapLogo from "@/components/PetsnapLogo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      if (attemptsRef.current >= 12) { setStatus("timeout"); return; }
      timerRef.current = setTimeout(poll, 2000);
    };
    poll();
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-grain flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center relative z-10">
        <div className="mx-auto mb-6"><PetsnapLogo className="w-14 h-14 mx-auto" /></div>
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-[#E76F51] animate-spin mx-auto mb-4" />
            <h1 className="font-display text-4xl font-bold text-[#2D232E]">Confirming your upgrade...</h1>
            <p className="font-hand text-2xl text-[#6A606C] mt-2">just a moment</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#2A9D8F]/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-[#2A9D8F]" />
            </div>
            <div className="inline-flex items-center gap-1.5 bg-[#E9C46A] text-[#2D232E] font-hand text-xl px-3 py-0.5 rounded-full mb-3">
              <Sparkles className="w-4 h-4" /> Pro unlocked
            </div>
            <h1 className="font-display text-4xl font-bold text-[#2D232E]">Welcome to Petsnap Pro!</h1>
            <p className="text-[#6A606C] mt-2">Unlimited pets and photos, priority AI, ad-free scrapbooks — all yours now.</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold px-6 py-3 shadow-[0_8px_20px_rgba(231,111,81,0.35)] transition-transform hover:scale-[1.02]"
            >
              Back to my pack
            </Link>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-[#E76F51] mx-auto mb-4" />
            <h1 className="font-display text-4xl font-bold text-[#2D232E]">
              {status === "timeout" ? "Still processing..." : "Payment didn't go through"}
            </h1>
            <p className="text-[#6A606C] mt-2">
              {status === "timeout"
                ? "Give it a minute — refresh this page or check your dashboard."
                : "Your card wasn't charged. Try again whenever you're ready."}
            </p>
            <Link
              to="/pricing"
              className="mt-6 inline-flex items-center rounded-full bg-white border border-[#EAE2D6] hover:border-[#E76F51] text-[#2D232E] font-semibold px-6 py-3"
            >
              Back to plans
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
